import { Box, CollapseProps, useTheme } from '@mui/material';
import RichText, { RichTextContent } from '../../../RichText';
import LightThemeProvider from '../../../../providers/LightThemeProvider';
import { useEffect, useRef, useState } from 'react';
import { Collapse } from '@mui/material';
import useIsMobile from '../../../../utils/useIsMobile';
import DarkThemeProvider from '../../../../providers/DarkThemeProvider';

type Props = {
  title: RichTextContent;
  description?: RichTextContent;
  isOpen: boolean;
  onClick: () => void;
  palletMode: 'light' | 'dark';
  elementPallet: 'light' | 'dark';
};

const ServiceElement = ({
  title,
  description,
  isOpen,
  onClick: _onClick,
  palletMode,
  elementPallet,
}: Props) => {
  const isMobile = useIsMobile();
  const timeoutRef = useRef<any>(null);
  const [opacity, setOpacity] = useState(+isOpen);
  const theme = useTheme();
  const ElementThemeProvider =
    elementPallet === 'light' ? LightThemeProvider : DarkThemeProvider;
  const OpenThemeProvider =
    palletMode === 'light' ? LightThemeProvider : DarkThemeProvider;

  const isAlternatePallet = palletMode !== theme.palette.mode;
  const borderColor = theme.palette.text.secondary;

  const MobileCollapse = isMobile ? Collapse : (p: CollapseProps) => p.children;
  const onClick = () => {
    _onClick();
    timeoutRef.current = setTimeout(() => {
      setOpacity(1);
    }, theme.transitions.duration.shortest);
  };

  useEffect(() => {
    if (!isOpen) {
      clearTimeout(timeoutRef.current);
      setOpacity(0);
    }
  }, [isOpen]);

  return (
    <ElementThemeProvider>
      <Box
        sx={{
          flexGrow: { md: isOpen ? 1 : 0 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'stretch',
          alignItems: 'stretch',
          transition: theme.transitions.create('all', {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Box
          sx={{
            p: 3,
            width: { xs: '100%', md: '180px' },
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            flexShrink: 0,
            bgcolor:
              !isAlternatePallet && isOpen
                ? 'background.paper'
                : 'background.default',
            color: 'text.primary',
            cursor: 'pointer',
            writingMode: { md: 'vertical-rl' },
            borderTop: `1px solid ${borderColor}`,
            borderRight: { md: `1px solid ${borderColor}` },
            '& > h3::after': {
              content: '""',
              position: 'absolute',
              top: { md: 0 },
              left: { md: 0 },
              right: { xs: 0, md: 'unset' },
              bottom: { xs: 0, md: 'unset' },
              height: { xs: '4px', md: 0 },
              width: { xs: 0, md: '4px' },
              bgcolor: 'secondary.main',
              transition: {
                xs: theme.transitions.create('width', {
                  duration: theme.transitions.duration.shortest,
                }),
                md: theme.transitions.create('height', {
                  duration: theme.transitions.duration.shortest,
                }),
              },
            },
            '&:hover > h3::after': {
              height: { md: '70%' },
              width: { xs: '70%', md: '4px' },
            },
          }}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }}
          onClick={onClick}
        >
          <RichText
            withTypography
            variant={'h2'}
            component={'h3'}
            sx={{
              transform: { md: `rotate(180deg)` },
              position: 'relative',
            }}
          >
            {title}
          </RichText>
        </Box>
        <OpenThemeProvider>
          <Box
            sx={{
              borderTop: `1px solid ${borderColor}`,
              bgcolor: isAlternatePallet
                ? 'background.default'
                : 'background.paper',
              width: { md: isOpen ? '100%' : '0px' },
              height: { xs: isOpen ? '100%' : '0px', md: '100%' },
              visibility: isOpen ? 'visible' : 'hidden',
              transition: {
                md: theme.transitions.create(['width'], {
                  duration: theme.transitions.duration.shortest,
                }),
                xs: theme.transitions.create(['height'], {
                  duration: theme.transitions.duration.shortest,
                }),
              },
            }}
          >
            <MobileCollapse
              in={isOpen}
              timeout={theme.transitions.duration.shortest}
            >
              <Box
                sx={{
                  height: { xs: '100%', md: opacity ? '100%' : 0 },
                  opacity: { xs: 1, md: opacity },
                  width: { xs: '100%', md: opacity ? '100%' : 0 },
                  p: { xs: 3, md: 4, lg: 7 },
                  pb: { md: 0 },
                  color: 'text.primary',
                  overflow: 'hidden',
                  transition: theme.transitions.create(['opacity'], {
                    duration: theme.transitions.duration.shortest,
                  }),
                }}
              >
                <RichText>{description}</RichText>
              </Box>
            </MobileCollapse>
          </Box>
        </OpenThemeProvider>
      </Box>
    </ElementThemeProvider>
  );
};

export default ServiceElement;
